import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Zap, AlertCircle, Calendar, CreditCard, RefreshCw, Info, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import axios from "axios";
import { toast } from "sonner";
import { AgentPaymentButton, isAgentLoggedIn } from "@/components/AgentPaymentFlow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TagihanListrik() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const customerId = searchParams.get("id");
  
  const [billData, setBillData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adminFee, setAdminFee] = useState(2000);

  useEffect(() => {
    if (!customerId) {
      navigate("/");
      return;
    }
    fetchBill();
    fetchAdminFee();
  }, [customerId]);

  const fetchAdminFee = async () => {
    try {
      const response = await axios.get(`${API}/admin/fees/tagihan_listrik`);
      setAdminFee(response.data.fee || 2000);
    } catch (err) {
      setAdminFee(2000);
    }
  };

  const fetchBill = async () => {
    try {
      setIsLoading(true);
      setError(null);
      // Try PLN Postpaid inquiry via DigiFlazz
      // SKU: "Pln Pascabayar" sesuai daftar produk di DigiFlazz
      const response = await axios.post(`${API}/digiflazz/inquiry`, {
        customer_no: customerId,
        buyer_sku_code: "PLNPOSTPAID" // SKU untuk tagihan listrik pascabayar (akan dicoba beberapa alternatif di backend)
      });
      setBillData(response.data);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Gagal mengambil data tagihan listrik. Pastikan ID pelanggan benar.";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const handlePayment = () => {
    if (!billData) return;
    navigate(`/payment-listrik?ref_id=${billData.ref_id}&customer_no=${customerId}&total=${billData.selling_price}`);
  };

  const totalBayar = billData ? (billData.selling_price || billData.price || 0) + adminFee : 0;

  return (
    <div className="min-h-screen hero-gradient">
      <header className="container mx-auto px-6 py-6">
        <nav className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/icons/icon-72x72.png" alt="Logo" className="w-10 h-10 rounded-xl" />
            <span className="text-xl font-heading font-semibold text-slate-800">Produk Digital</span>
          </div>
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Kembali
            </Button>
          </Link>
        </nav>
      </header>

      <main className="container mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="max-w-3xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-500 to-amber-600 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-heading font-bold text-slate-900">
                    Tagihan Listrik PLN
                  </h1>
                  <p className="text-slate-600 text-sm">
                    ID Pelanggan: <span className="font-semibold text-yellow-600">{customerId}</span>
                  </p>
                </div>
              </div>
            </div>

            {isLoading ? (
              <div className="space-y-4">
                <Card className="glass-card-solid">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Loader2 className="w-5 h-5 animate-spin text-yellow-600" />
                      <span className="text-slate-600">Mengambil data tagihan dari DigiFlazz...</span>
                    </div>
                    <Skeleton className="h-6 w-48 mb-4" />
                    <Skeleton className="h-4 w-full mb-2" />
                    <Skeleton className="h-4 w-2/3 mb-4" />
                    <Skeleton className="h-20 w-full" />
                  </CardContent>
                </Card>
              </div>
            ) : error ? (
              <div className="space-y-4">
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
                
                <div className="glass-card rounded-2xl p-8 text-center">
                  <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                  <h2 className="text-xl font-heading font-semibold text-slate-800 mb-2">
                    Gagal Mengambil Data
                  </h2>
                  <p className="text-slate-600 mb-4">{error}</p>
                  <p className="text-sm text-slate-500 mb-6">
                    Kemungkinan penyebab:
                    <br />• ID pelanggan tidak valid
                    <br />• Tidak ada tagihan yang belum dibayar
                    <br />• Layanan PLN Postpaid belum tersedia di DigiFlazz Anda
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button onClick={fetchBill} className="rounded-full gap-2 bg-yellow-500 hover:bg-yellow-600">
                      <RefreshCw className="w-4 h-4" />
                      Coba Lagi
                    </Button>
                    <Link to="/">
                      <Button variant="outline" className="rounded-full">
                        Kembali ke Beranda
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ) : billData ? (
              <>
                {/* Customer Info Card */}
                <Card className="glass-card-solid mb-6">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <p className="text-sm text-slate-500">Nama Pelanggan</p>
                        <p className="font-heading font-semibold text-slate-800 text-lg">
                          {billData.customer_name || "-"}
                        </p>
                      </div>
                      <span className="px-3 py-1 rounded-full bg-yellow-100 text-yellow-700 text-sm font-medium">
                        {billData.lembar_tagihan || 1} Lembar
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">ID Pelanggan</p>
                        <p className="font-medium text-slate-800">{billData.customer_no}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Tarif/Daya</p>
                        <p className="font-medium text-slate-800">{billData.tarif || "-"}</p>
                      </div>
                      {billData.alamat && (
                        <div className="col-span-2">
                          <p className="text-slate-500">Alamat</p>
                          <p className="font-medium text-slate-800">{billData.alamat}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Bill Details */}
                {billData.detail && billData.detail.length > 0 ? (
                  <div className="space-y-3 mb-6">
                    <h3 className="font-heading font-semibold text-slate-800">Detail Tagihan</h3>
                    {billData.detail.map((item, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                      >
                        <Card className="bill-card">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-slate-400" />
                                <span className="font-medium text-slate-800">{item.periode}</span>
                              </div>
                              <span className="font-semibold text-yellow-600">
                                {formatCurrency(item.nilai_tagihan)}
                              </span>
                            </div>
                            {(item.admin > 0 || item.denda > 0) && (
                              <div className="grid grid-cols-2 gap-2 text-sm text-slate-500">
                                {item.admin > 0 && (
                                  <span>Admin: {formatCurrency(item.admin)}</span>
                                )}
                                {item.denda > 0 && (
                                  <span className="text-red-600">Denda: {formatCurrency(item.denda)}</span>
                                )}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <Card className="glass-card-solid mb-6">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <span className="font-medium text-slate-800">{billData.periode || "Periode"}</span>
                        </div>
                        <span className="font-semibold text-yellow-600">{formatCurrency(billData.price)}</span>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Payment Summary */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <Card className="glass-card border-2 border-yellow-200">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                          <CreditCard className="w-5 h-5 text-yellow-600" />
                        </div>
                        <div>
                          <h3 className="font-heading font-semibold text-slate-800">
                            Total Pembayaran
                          </h3>
                          <p className="text-sm text-slate-500">
                            Termasuk biaya admin
                          </p>
                        </div>
                      </div>

                      <div className="bg-slate-50 rounded-xl p-4 mb-4">
                        <div className="space-y-2 text-sm mb-3">
                          <div className="flex justify-between">
                            <span className="text-slate-500">Tagihan Listrik</span>
                            <span className="font-medium">{formatCurrency(billData.selling_price || billData.price)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Biaya Admin</span>
                            <span className="font-medium text-sky-600">{formatCurrency(adminFee)}</span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center pt-3 border-t border-slate-200">
                          <span className="font-semibold text-slate-800">Total Bayar</span>
                          <span className="text-2xl font-heading font-bold text-yellow-600">
                            {formatCurrency(totalBayar)}
                          </span>
                        </div>
                      </div>

                      {isAgentLoggedIn() ? (
                        <AgentPaymentButton
                          serviceType="tagihan_listrik"
                          customerNo={customerId}
                          customerName={billData.customer_name || ""}
                          customerAddress={billData.alamat || ""}
                          productName={`Tagihan Listrik ${billData.periode || ""}`}
                          amount={billData.selling_price || billData.price || 0}
                          adminFee={adminFee}
                          buyerSkuCode={billData.buyer_sku_code || "PP_PLN"}
                          billDetails={billData.detail || [{
                            periode: billData.periode,
                            nilai_tagihan: billData.price
                          }]}
                          onSuccess={() => { 
                            toast.success("Pembayaran tagihan listrik berhasil!");
                          }}
                          onReceiptClose={() => {
                            navigate("/");
                          }}
                        />
                      ) : (
                        <>
                          <Alert className="mb-4 bg-yellow-50 border-yellow-200">
                            <Info className="h-4 w-4 text-yellow-600" />
                            <AlertDescription className="text-yellow-800 text-sm">
                              Pembayaran dilakukan via Transfer Bank. Setelah transfer, upload bukti pembayaran.
                            </AlertDescription>
                          </Alert>

                          <Button
                            onClick={handlePayment}
                            className="w-full h-14 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all bg-gradient-to-r from-yellow-500 to-amber-600"
                            data-testid="pay-listrik-btn"
                          >
                            Bayar Sekarang
                          </Button>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              </>
            ) : (
              <div className="glass-card rounded-2xl p-12 text-center">
                <Zap className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                <h2 className="text-xl font-heading font-semibold text-slate-800 mb-2">
                  Tidak Ada Tagihan
                </h2>
                <p className="text-slate-600 mb-6">
                  Tidak ada tagihan listrik yang ditemukan untuk ID ini.
                </p>
                <Link to="/">
                  <Button className="rounded-full bg-yellow-500 hover:bg-yellow-600">
                    Kembali ke Beranda
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
}
